"use strict";
/**
 * Kit-Compatible Signer Utilities
 *
 * Framework-agnostic utility functions for Kit signer implementation.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.detectMessageModification = detectMessageModification;
exports.updateSignatureDictionary = updateSignatureDictionary;
exports.publicKeyToAddress = publicKeyToAddress;
exports.publicKeyToString = publicKeyToString;
exports.freezeSigner = freezeSigner;
var kit_1 = require("@solana/kit");
/**
 * Detect if a message was modified during signing
 *
 * @param original - Original message bytes
 * @param modified - Potentially modified message bytes
 * @returns true if message was modified, false otherwise
 */
function detectMessageModification(original, modified) {
    if (original.length !== modified.length) {
        return true;
    }
    for (var i = 0; i < original.length; i++) {
        if (original[i] !== modified[i]) {
            return true;
        }
    }
    return false;
}
/**
 * Update signature dictionary with new signature
 *
 * If message was modified, clears existing signatures (they're now invalid).
 * Otherwise, merges new signature with existing ones.
 *
 * @param original - Original message content
 * @param signed - Signed message content
 * @param originalSignatures - Original signature dictionary
 * @param address - Address of the signer
 * @param signature - New signature bytes
 * @returns Updated signature dictionary
 */
function updateSignatureDictionary(original, signed, originalSignatures, address, signature) {
    var _a, _b;
    var wasModified = detectMessageModification(original, signed);
    // If message was modified, existing signatures are invalid
    if (wasModified) {
        return _a = {}, _a[address] = signature, _a;
    }
    // Message unchanged, add to existing signatures
    return __assign(__assign({}, originalSignatures), (_b = {}, _b[address] = signature, _b));
}
/**
 * Convert web3.js PublicKey to Kit Address type
 *
 * @param publicKey - Web3.js PublicKey instance or null
 * @returns Kit Address or null
 */
function publicKeyToAddress(publicKey) {
    if (!publicKey) {
        return null;
    }
    try {
        return (0, kit_1.address)(publicKey.toBase58());
    }
    catch (error) {
        console.error('Failed to convert PublicKey to Address:', error);
        return null;
    }
}
/**
 * Convert web3.js PublicKey to address string
 *
 * @param publicKey - Web3.js PublicKey instance or null
 * @returns Base58 address string or null
 */
function publicKeyToString(publicKey) {
    if (!publicKey) {
        return null;
    }
    try {
        return publicKey.toBase58();
    }
    catch (error) {
        console.error('Failed to convert PublicKey to string:', error);
        return null;
    }
}
/**
 * Create a frozen (immutable) signer object
 *
 * This ensures referential stability and prevents accidental mutations.
 *
 * @param signer - Signer object to freeze
 * @returns Frozen signer object
 */
function freezeSigner(signer) {
    return Object.freeze(signer);
}
